import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootBlogPostCardProps } from "./TootBlogPostCard.props";

const Image = resolveTootPrimitive("Image");
const Link = resolveTootPrimitive("Link");

const dateFormatter = new Intl.DateTimeFormat("en", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const formatDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

export const TootBlogPostCard = ({
  className,
  post,
  priority = false,
  variant = "standard",
}: ITootBlogPostCardProps) => {
  const featured = variant !== "standard";
  const prominent = variant === "featured";
  const imageSrc = post.image?.src ?? post.image?.url;
  const publishedDate = formatDate(post.publishedAt);

  return (
    <article
      className={cn("group relative min-w-0", featured ? "h-full" : "flex h-full", className)}
    >
      <div
        className={cn(
          "relative flex h-full min-w-0 overflow-hidden rounded-[var(--toot-radius-card)] border transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none",
          featured
            ? "min-h-[22rem] flex-col justify-end border-toot-grid-strong bg-toot-surface text-white shadow-[0_20px_54px_rgba(23,25,29,0.16)] hover:-translate-y-1 hover:shadow-[0_28px_68px_rgba(23,25,29,0.22)] motion-reduce:hover:translate-y-0"
            : "flex-1 flex-col border-toot-grid bg-card text-toot-foreground shadow-[0_12px_34px_rgba(23,25,29,0.08)] hover:-translate-y-1 hover:border-toot-orange/55 hover:shadow-[0_20px_48px_rgba(23,25,29,0.12)] motion-reduce:hover:translate-y-0",
        )}
      >
        {featured ? (
          <>
            {imageSrc ? (
              <Image
                alt={post.image?.alt ?? ""}
                className="absolute inset-0 h-full w-full"
                height={post.image?.height}
                imageClassName="h-full w-full !object-cover transition-transform duration-700 group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                loading={priority ? "eager" : "lazy"}
                sizes={
                  prominent ? "(min-width: 1024px) 62vw, 100vw" : "(min-width: 1024px) 34vw, 100vw"
                }
                src={imageSrc}
                srcSet={post.image?.srcSet}
                width={post.image?.width}
              />
            ) : null}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,17,21,0.48)_0%,rgba(15,17,21,0.7)_48%,rgba(15,17,21,0.97)_100%)]"
            />
            <div className={cn("relative z-10", prominent ? "p-6 sm:p-8 lg:p-10" : "p-6 sm:p-7")}>
              <div className="mb-4 flex flex-wrap items-center gap-3 text-xs font-black text-white/90">
                <span className="rounded-full border border-white/30 bg-white/10 px-3 py-1.5">
                  Featured article
                </span>
                {publishedDate ? <time dateTime={post.publishedAt}>{publishedDate}</time> : null}
              </div>
              <h3
                className={cn(
                  "max-w-[18ch] text-balance font-black leading-[1.05] tracking-[-0.035em]",
                  prominent ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl",
                )}
              >
                {post.title}
              </h3>
              {post.excerpt ? (
                <div
                  className="mt-4 line-clamp-3 max-w-[60ch] text-pretty text-sm leading-6 text-white/85 sm:text-base sm:leading-7 [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              ) : null}
              <span className="mt-6 inline-flex min-h-11 items-center text-sm font-black">
                Read article{" "}
                <span
                  aria-hidden="true"
                  className="ml-2 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                >
                  →
                </span>
              </span>
            </div>
          </>
        ) : (
          <>
            {imageSrc ? (
              <div className="relative aspect-[16/10] overflow-hidden border-b border-toot-grid bg-muted">
                <Image
                  alt={post.image?.alt ?? ""}
                  className="h-full w-full"
                  height={post.image?.height}
                  imageClassName="h-full w-full !object-cover transition-transform duration-500 group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  loading={priority ? "eager" : "lazy"}
                  sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
                  src={imageSrc}
                  srcSet={post.image?.srcSet}
                  width={post.image?.width}
                />
              </div>
            ) : null}
            <div className="flex min-h-56 flex-1 flex-col p-5 sm:p-6">
              {publishedDate ? (
                <time
                  className="mb-4 text-xs font-bold text-toot-muted"
                  dateTime={post.publishedAt}
                >
                  {publishedDate}
                </time>
              ) : null}
              <h3 className="text-balance text-2xl font-black leading-tight tracking-[-0.035em] text-toot-foreground">
                {post.title}
              </h3>
              {post.excerpt ? (
                <div
                  className="mt-3 line-clamp-3 text-sm leading-7 text-toot-muted [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              ) : null}
              <span className="mt-auto inline-flex min-h-11 items-center pt-6 text-sm font-black text-toot-orange">
                Read article{" "}
                <span
                  aria-hidden="true"
                  className="ml-2 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                >
                  →
                </span>
              </span>
            </div>
          </>
        )}
        <Link
          aria-label={`Read ${post.title}`}
          className="absolute inset-0 z-20 cursor-pointer rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          href={post.href}
        />
      </div>
    </article>
  );
};

TootBlogPostCard.displayName = "Content::TootBlogPostCard";
